import { FormEvent, useRef, useState } from 'react';
import { submitTaxExemptForm, type OrgAddressInput } from '../api';

interface Props {
  orgId: string;
  orgName: string;
  token: string;
  initial: OrgAddressInput;
  contactEmail: string;
}

type Status =
  | { kind: 'idle' }
  | { kind: 'submitting' }
  | { kind: 'success' }
  | { kind: 'error'; message: string };

const MAX_FILE_BYTES = 5 * 1024 * 1024;

export function SubmissionForm({ orgId, orgName, token, initial, contactEmail }: Props) {
  const [address, setAddress] = useState<OrgAddressInput>(initial);
  const [file, setFile] = useState<File | null>(null);
  const [status, setStatus] = useState<Status>({ kind: 'idle' });
  const fileInputRef = useRef<HTMLInputElement>(null);

  function update<K extends keyof OrgAddressInput>(field: K, value: string) {
    setAddress((prev) => ({ ...prev, [field]: value }));
  }

  function onFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0] ?? null;
    if (!f) {
      setFile(null);
      return;
    }
    if (f.type !== 'application/pdf') {
      setStatus({ kind: 'error', message: 'The uploaded file must be a PDF.' });
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    if (f.size > MAX_FILE_BYTES) {
      setStatus({ kind: 'error', message: 'File is too large. Max size is 5 MB.' });
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    setStatus({ kind: 'idle' });
    setFile(f);
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setStatus({ kind: 'submitting' });
    try {
      await submitTaxExemptForm(orgId, token, address, file);
      setStatus({ kind: 'success' });
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch {
      setStatus({ kind: 'error', message: 'Something went wrong. Please try again.' });
    }
  }

  const submitting = status.kind === 'submitting';
  const showSuccess = status.kind === 'success';
  const showError = status.kind === 'error';

  return (
    <section
      aria-labelledby="submission-heading"
      className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm sm:p-8"
    >
      <h2 id="submission-heading" className="text-lg font-semibold text-slate-900">
        Sales tax exemption submission
      </h2>
      {(orgName || orgId) && (
        <p className="mt-1 text-sm text-slate-700">
          {orgName ? <span className="font-medium">{orgName}</span> : null}
          {orgName && orgId ? <span className="text-slate-400"> · </span> : null}
          {orgId ? <span className="text-slate-500">#{orgId}</span> : null}
        </p>
      )}
      <p className="mt-1 text-sm text-slate-600">
        Confirm or update the taxable address on file for your organization, and
        optionally upload a PDF copy of your tax-exemption certificate.
      </p>

      <form onSubmit={onSubmit} className="mt-5 space-y-6">
        {/* Address fields */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field
            id="orgAddress"
            label="Address"
            value={address.orgAddress}
            onChange={(v) => update('orgAddress', v)}
            disabled={submitting}
            required
            className="sm:col-span-2"
          />
          <Field
            id="orgAddress2"
            label="Address line 2"
            value={address.orgAddress2}
            onChange={(v) => update('orgAddress2', v)}
            disabled={submitting}
            className="sm:col-span-2"
          />
          <Field
            id="orgCity"
            label="City"
            value={address.orgCity}
            onChange={(v) => update('orgCity', v)}
            disabled={submitting}
            required
          />
          <Field
            id="orgState"
            label="State / Province"
            value={address.orgState}
            onChange={(v) => update('orgState', v)}
            disabled={submitting}
            required
          />
          <Field
            id="orgZip"
            label="Zip / Postal code"
            value={address.orgZip}
            onChange={(v) => update('orgZip', v)}
            disabled={submitting}
            required
          />
          <Field
            id="orgCountry"
            label="Country"
            value={address.orgCountry}
            onChange={(v) => update('orgCountry', v)}
            disabled={submitting}
            required
          />
        </div>

        {/* File upload */}
        <div className="border-t border-slate-200 pt-5">
          <label htmlFor="file" className="block text-sm font-medium text-slate-700">
            Tax-exemption certificate
            <span className="ml-1 text-xs font-normal text-slate-400">(optional)</span>
          </label>
          <p className="mt-1 text-xs text-slate-500">PDF, max 5 MB.</p>
          <input
            ref={fileInputRef}
            id="file"
            name="file"
            type="file"
            accept="application/pdf"
            disabled={submitting}
            onChange={onFileChange}
            className="mt-2 block w-full text-sm text-slate-700 file:mr-3 file:rounded-md file:border-0 file:bg-slate-100 file:px-3 file:py-2 file:text-sm file:font-medium file:text-slate-700 hover:file:bg-slate-200"
          />
        </div>

        {/* Submit */}
        <div>
          <button
            type="submit"
            disabled={submitting}
            className="btn-brand inline-flex items-center justify-center rounded-md px-4 py-2 text-sm font-medium text-white transition"
          >
            {submitting ? 'Submitting…' : 'Submit'}
          </button>
        </div>
      </form>

      {showSuccess && (
        <p
          role="status"
          className="mt-4 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800"
        >
          Your submission has been received and will be reviewed by our team.
        </p>
      )}
      {showError && (
        <p
          role="alert"
          className="mt-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800"
        >
          {status.message}
          {contactEmail ? (
            <>
              {' '}
              You can also contact{' '}
              <a className="underline" href={`mailto:${contactEmail}`}>
                {contactEmail}
              </a>
              .
            </>
          ) : null}
        </p>
      )}
    </section>
  );
}

interface FieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
  required?: boolean;
  className?: string;
}

function Field({ id, label, value, onChange, disabled, required, className }: FieldProps) {
  return (
    <div className={className}>
      <label htmlFor={id} className="block text-sm font-medium text-slate-700">
        {label}
        {required ? null : (
          <span className="ml-1 text-xs font-normal text-slate-400">(optional)</span>
        )}
      </label>
      <input
        id={id}
        name={id}
        type="text"
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-transparent disabled:bg-slate-50"
      />
    </div>
  );
}
